import React from "react";
import { usePartnerSchools } from "../hooks/usePartnerSchools";
import { PartnerSchool } from "../types/partnerSchools";

/**
 * "Trusted By Our Partner Schools" landing page section.
 *
 * Renders a 6-per-row grid of school logos that wraps as needed, read from the CMS.
 * When the CMS returns no logos (or is unavailable) nothing is rendered at all
 * - no heading, no grid, no placeholders.
 */
function PartnerSchoolLogo({ school }: { school: PartnerSchool }) {
    const logo = (
        <img
            src={school.imageUrl}
            alt={school.title}
            className="landing-trusted-logo-img"
            loading="lazy"
        />
    );

    if (school.websiteUrl) {
        return (
            <a
                className="landing-trusted-logo landing-trusted-logo--link"
                href={school.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={school.title}
            >
                {logo}
            </a>
        );
    }

    return <div className="landing-trusted-logo">{logo}</div>;
}

export function PartnerSchools() {
    const { schools, loading, error } = usePartnerSchools();

    // Fail closed on the UI: while loading, on error, or with no logos,
    // render nothing so no title/placeholders appear.
    if (loading || error || schools.length === 0) {
        return null;
    }

    return (
        <section className="landing-trusted">
            <div className="landing-trusted-inner">
                <h2 className="landing-trusted-heading">Trusted By Our Partner Schools</h2>
                <div className="landing-trusted-grid">
                    {schools.map((school) => (
                        <PartnerSchoolLogo key={school.id} school={school} />
                    ))}
                </div>
            </div>
        </section>
    );
}

export default PartnerSchools;
